// LicenseManager.jsx
// -----------------------------------------------------------
// Abschnitt: Lizenzverwaltung
// -----------------------------------------------------------

import { useState, useRef, useEffect } from "react";
import { FiKey, FiAlertTriangle, FiAward, FiCheckCircle, FiXCircle, FiExternalLink } from "react-icons/fi";
import { useLicenseContext } from "../contexts/LicenseContext";
import { useToast } from "../contexts/ToastContext";
import { useI18n } from "../contexts/I18nContext";

export default function LicenseManager() {
  const { t } = useI18n();
  const {
    license,
    loading,
    errorMsg,
    successMsg,
    isInitialLoadSuccess,
    activateLicense,
    reloadLicenseStatus,
    clearError,
  } = useLicenseContext();
  const { showToast } = useToast();

  const [inputKey, setInputKey] = useState("");
  const [showActivationForm, setShowActivationForm] = useState(false);
  const inputRef = useRef(null);

  // Route hook-level messages to toast
  useEffect(() => {
    if (errorMsg) {
      showToast(errorMsg, "error", 0); // duration 0 = stays until dismissed
      clearError();
    }
  }, [errorMsg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (successMsg) {
      showToast(successMsg, "success");
    }
  }, [successMsg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showActivationForm && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showActivationForm]);

  const handleActivate = async () => {
    if (!inputKey.trim()) return;
    const ok = await activateLicense(inputKey.trim());
    if (ok) {
      setInputKey("");
      setShowActivationForm(false);
    }
  };

  const handleCancel = async () => {
    setShowActivationForm(false);
    setInputKey("");
    await reloadLicenseStatus();
  };

  // Modus A: Keine Lizenz oder Aktivierungsformular
  if (!license || showActivationForm) {
    return (
      <div style={cardStyle}>
        <h3><FiKey size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Lizenz aktivieren")}</h3>

        {!isInitialLoadSuccess && (
          <div style={warnBoxStyle}>
            <FiAlertTriangle size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut.")}
          </div>
        )}

        <label style={{ display: "block", marginBottom: "8px" }}>
          {t("Bitte geben Sie Ihren Lizenzschlüssel ein:")}
        </label>
        <input
          ref={inputRef}
          type="text"
          placeholder="AE-XXXX-XXXX-XXXX"
          value={inputKey}
          onChange={(e) => setInputKey(e.target.value)}
          style={inputStyle}
        />

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={handleActivate}
            disabled={loading || !isInitialLoadSuccess}
            style={btnActivate(loading || !isInitialLoadSuccess)}
          >
            {loading ? t("Aktivieren...") : t("Aktivieren")}
          </button>
          {license && (
            <button onClick={handleCancel} disabled={loading} style={btnGray(loading)}>
              {t("Abbrechen")}
            </button>
          )}
        </div>

      </div>
    );
  }

  // Modus B: Lizenz vorhanden
  return (
    <div style={cardStyle}>
      <h3 style={{ marginBottom: "16px" }}><FiAward size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Ihre Lizenzinformationen")}</h3>

      <table style={{ width: "100%", marginBottom: "16px", fontSize: "13px", color: "#555" }}>
        <tbody>
          <tr>
            <td style={{ paddingRight: "12px", paddingBottom: "3px", fontWeight: "500", color: "#444" }}>{t("Lizenztyp:")}</td>
            <td style={{ paddingBottom: "3px" }}>{license.lizenzTyp}</td>
          </tr>
          <tr>
            <td style={{ paddingRight: "12px", paddingBottom: "3px", fontWeight: "500", color: "#444" }}>{t("Lizenzschlüssel:")}</td>
            <td style={{ paddingBottom: "3px" }}>{license.lizenzKey}</td>
          </tr>
          <tr>
            <td style={{ paddingRight: "12px", paddingBottom: "3px", fontWeight: "500", color: "#444" }}>{t("Status:")}</td>
            <td style={{ paddingBottom: "3px" }}>{license.istAktiv ? <><FiCheckCircle size={13} style={{ marginRight: "4px", verticalAlign: "middle", color: "#28a745" }} />{t("Aktiv")}</> : <><FiXCircle size={13} style={{ marginRight: "4px", verticalAlign: "middle", color: "#dc3545" }} />{t("Inaktiv")}</>}</td>
          </tr>
          <tr>
            <td style={{ paddingRight: "12px", paddingBottom: "3px", fontWeight: "500", color: "#444" }}>{t("Max E-Mails/Tag:")}</td>
            <td style={{ paddingBottom: "3px" }}>{license.maxEmailsProTag}</td>
          </tr>
          <tr>
            <td style={{ paddingRight: "12px", fontWeight: "500", color: "#444" }}>{t("Max E-Mails/Monat:")}</td>
            <td>{license.maxEmailsProMonat}</td>
          </tr>
        </tbody>
      </table>

      <button
        onClick={() => setShowActivationForm(true)}
        style={btnReactivate}
      >
        {t("Lizenz neu aktivieren")}
      </button>

      {license.lizenzTyp === "Free" && (
        <p style={{ marginTop: "12px", fontSize: "0.9em", color: "#555" }}>
          <FiExternalLink size={13} style={{ marginRight: "4px", verticalAlign: "middle" }} />{t("Noch keine Lizenz? Jetzt anfragen unter")}{" "}
          <a
            href="https://avamboo-encrypt.com/avamboo-encrypt-anfrage/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0078d7" }}
          >
            avamboo-encrypt.com
          </a>
        </p>
      )}
    </div>
  );
}

const cardStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  border: "1px solid #e0e0e0",
  boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
  padding: "16px 14px",
};

const warnBoxStyle = {
  background: "#fff3cd",
  border: "1px solid #ffc107",
  padding: "8px",
  borderRadius: "6px",
  marginBottom: "10px",
  color: "#856404",
};

const inputStyle = {
  width: "100%",
  padding: "8px",
  marginBottom: "10px",
  borderRadius: "5px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
  fontSize: "13px",
};

const btnActivate = (disabled) => ({
  width: "50%",
  padding: "10px 24px",
  borderRadius: "6px",
  background: disabled ? "#ccc" : "#0078d7",
  color: "#fff",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  whiteSpace: "nowrap",
  fontWeight: "600",
  fontSize: "14px",
});

const btnGray = (disabled) => ({
  width: "50%",
  padding: "10px 24px",
  borderRadius: "6px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  whiteSpace: "nowrap",
  fontWeight: "600",
  fontSize: "14px",
});

const btnReactivate = {
  width: "100%",
  padding: "10px 24px",
  borderRadius: "6px",
  background: "#0078d7",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontWeight: "600",
  fontSize: "14px",
};
